import { ApolloLink, Observable } from '@apollo/client/core';
import type { FetchResult, Operation } from '@apollo/client/core';
import { print } from 'graphql';
import echo from '@/services/echo';
import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';

export class PusherLink extends ApolloLink {
  constructor() {
    super();
  }

  public request(operation: Operation): Observable<FetchResult> | null {
    return new Observable((observer) => {
      // 1. Send the subscription query via HTTP to get the channel name
      // We manually fetch because we need to inspect the 'extensions' in the response.
      // Standard Apollo fetch logic or a simple fetch:

      const uri = import.meta.env.VITE_BACKEND_BASE_URL; // Or derive from operation context if needed
      const token = localStorage.getItem(LocalStorageKeys.token);
      const operationName = operation.operationName;

      const performSubscription = async() => {
        try {
          const currentSchoolId = localStorage.getItem(LocalStorageKeys.currentSchoolId);
          const response = await fetch(uri, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              ...(currentSchoolId ? { 'X-Current-School-ID': currentSchoolId } : {}),
              'Apollo-Require-Preflight': 'true',
            },
            body: JSON.stringify({
              query: print(operation.query),
              variables: operation.variables,
            }),
          }).catch(err => {
            console.warn('PusherLink: Failed to register subscription (Backend unreachable?).', err);
            throw err;
          });

          if (!response.ok) {
            // Convert HTTP error to throw
            throw new Error(`Subscription registration failed: ${response.statusText}`);
          }

          const result = await response.json();

          if (result.errors) {
            console.warn('PusherLink: Subscription registration returned GraphQL errors.', result.errors);
            observer.error(result.errors);
            return;
          }

          // 2. Extract Channel Name from Lighthouse Extensions
          const channelName = result.extensions?.lighthouse_subscriptions?.channel;

          if (!channelName) {
            console.warn('Lighthouse Subscription: No channel returned in response extensions.', result);
            if (result.data) {
              observer.next(result);
            }
            // Graceful degradation: just stop if no channel
            return;
          }

          // 3. Subscribe to the Channel via Echo
          let echoChannel;
          if (channelName.startsWith('private-')) {
            echoChannel = echo.private(channelName.replace(/^private-/, ''));
          } else if (channelName.startsWith('presence-')) {
            echoChannel = echo.join(channelName.replace(/^presence-/, ''));
          } else {
            echoChannel = echo.channel(channelName);
          }

          // 4. Listen for Events
          echoChannel.listen('.lighthouse-subscription', (payload: unknown) => {
            // If payload is a string, try to parse it
            let data: unknown = payload;
            if (typeof payload === 'string') {
              try {
                data = JSON.parse(payload);
              } catch (e) {
                console.error('PusherLink: Failed to parse payload string', e);
              }
            }

            // Lighthouse sends the result wrapped in { more: boolean, result: ExecutionResult }
            const parsed = data as Record<string, unknown> | null;
            if (parsed && parsed.result) {
              observer.next(parsed.result);
            } else {
              // Fallback
              observer.next({
                data: {
                  [operationName ? operationName.charAt(0).toLowerCase() + operationName.slice(1) : 'data']: data,
                },
              });
            }
          });

          // Clean up on unsubscribe
          return () => {
            echo.leave(channelName);
          };

        } catch (error: unknown) {
          console.warn('PusherLink: Real-time subscription failed.', error);
          // Do NOT emit error to observer if we want to avoid crashing components that don't catch it.
          // Just log it. The subscription will just be "dead".
          // observer.error(error);
        }
      };

      // Wrap the async execution
      const subscription = performSubscription();

      // Return teardown logic (imperfect here since subscription is async,
      // but if observer.unsubscribe() is called, we want to ensure we leave channel)
      // We need to capture the channel name to leave it.
      // Since `performSubscription` is async, we can't return the cleanup fn immediately from it.
      // We can rely on the fact that we subscribed inside.
      // A better structure might be needed if cancellation happens rapidly.
    });
  }
}
